import { forwardRef, type ReactNode } from 'react';

import { accessValue } from '../../../../core/utils/accessors/access-value.js';
import { DEFAULT_VALUE_ACCESSOR } from '../../../core/constants.js';
import type { ComponentWithName } from '../../../core/types/component-with-name.js';
import { useGaugeChartColorContext } from '../../hooks/useGaugeChartColorContext.js';
import { useGaugeChartLoadingContext } from '../../hooks/useGaugeChartLoadingContext.js';
import { useGaugeChartRangeMaxContext } from '../../hooks/useGaugeChartRangeMaxContext.js';
import { useGaugeChartRangeMinContext } from '../../hooks/useGaugeChartRangeMinContext.js';
import type { GaugeChartData } from '../../types/gauge-chart.js';
import { GaugeBaseArc } from '../GaugeBaseArc.js';
import { GaugeLabels } from '../GaugeLabels.js';
import { GaugeChartOverlay } from '../GaugeLayoutOverlay/GaugeLayoutOverlay.js';
import { GaugeRootSVG } from '../GaugeRootSVG.js';
import { GaugeThresholdIntervalArcs } from '../GaugeThresholdIntervalArcs.js';
import { GaugeValueArc } from '../GaugeValueArc.js';
import { layoutContainer } from './GaugeChartLayout.sty.js';
import { type GaugeChartToolbarProps } from './GaugeChartToolbar.js';

export interface GaugeChartLayoutProps {
  value: number | GaugeChartData;
  valueAccessor?: string;
  prefixIcon?: ReactNode;
  intents: GaugeChartToolbarProps['intents'];
  toolbar: GaugeChartToolbarProps['toolbar'];
}

export const GaugeChartLayout = forwardRef<
  HTMLDivElement,
  GaugeChartLayoutProps
>(({ value, prefixIcon, intents, toolbar, valueAccessor }, forwardedRef) => {
  const min = useGaugeChartRangeMinContext();
  const max = useGaugeChartRangeMaxContext();
  const valueArcColor = useGaugeChartColorContext();
  const showLoading = useGaugeChartLoadingContext();

  const displayValue =
    typeof value === 'number'
      ? value
      : (accessValue<number>(value, valueAccessor ?? DEFAULT_VALUE_ACCESSOR) ??
        0);

  const toolbarConfig: typeof toolbar = {
    ...toolbar,
    hidden: toolbar?.hidden || showLoading,
  };

  return (
    <div
      data-testid="gauge-layout-container"
      ref={forwardedRef}
      className={layoutContainer}
    >
      <GaugeChartOverlay
        value={displayValue}
        color={valueArcColor}
        intents={intents}
        showLoading={showLoading}
        toolbar={toolbarConfig}
      >
        <GaugeRootSVG>
          <GaugeBaseArc />
          <GaugeValueArc
            value={displayValue}
            color={valueArcColor}
            min={min}
            max={max}
          />
          <GaugeThresholdIntervalArcs />
          <GaugeLabels value={displayValue} prefixIcon={prefixIcon} />
        </GaugeRootSVG>
      </GaugeChartOverlay>
    </div>
  );
});

(GaugeChartLayout as ComponentWithName<typeof GaugeChartLayout>).displayName =
  'GaugeChartLayout';
